import { api } from './api';

export interface Sale {
  id: string;
  locationId: string;
  userId: string;
  customerName?: string;
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER' | 'CHECK';
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  isDraft: boolean;
  draftName?: string;
  discountType?: string;
  discountName?: string;
  discountPercentage?: number;
  discountFixedAmount?: number;
  discountAmount: number;
  discountIsManual: boolean;
  createdAt: string;
  updatedAt: string;
  user: {
    name: string;
    employeeCode?: string;
  };
  location?: {
    id: string;
    name: string;
  };
  saleItems: SaleItem[];
  commission?: {
    amount: number;
    percentage: number;
    isPaid: boolean;
    user: {
      name: string;
      employeeCode?: string;
    };
  };
}

export interface SaleItem {
  id: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  total: number;
  product: {
    name: string;
    sku: string;
  };
}

export interface CreateSaleData {
  customerName?: string;
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER' | 'CHECK';
  employeeCode?: string;
  items: {
    productId: string;
    quantity: number;
    unitPrice: number;
    // Optional fields for custom products
    isCustomProduct?: boolean;
  }[];
  // Optional discount information
  discount?: {
    type: string;
    name: string;
    percentage?: number;
    fixedAmount?: number;
    isManual?: boolean;
    amount: number;
  };
}

export interface CreateDraftData {
  draftName?: string;
  customerName?: string;
  items: {
    productId: string;
    quantity: number;
    unitPrice: number;
  }[];
  discount?: {
    type: string;
    name?: string;
    value: number;
    isManual?: boolean;
  };
}

export interface UpdateDraftData extends CreateDraftData {
  id: string;
}

export interface CompleteDraftData {
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER' | 'CHECK';
  employeeCode?: string;
}

export interface DraftsResponse {
  success: boolean;
  data: Sale[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface SalesResponse {
  success: boolean;
  sales: Sale[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface SalesSummary {
  success: boolean;
  summary: {
    total: {
      amount: number;
      count: number;
    };
    byPaymentMethod: Array<{
      paymentMethod: string;
      _sum: { total: number };
      _count: { id: number };
    }>;
    byDay: Array<{
      createdAt: string;
      _sum: { total: number };
      _count: { id: number };
    }>;
    topProducts: Array<{
      productId: string;
      _sum: { quantity: number; total: number };
    }>;
  };
}

export const salesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSales: builder.query<SalesResponse, {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
      cashierId?: string;
      paymentMethod?: string;
      status?: string;
      locationId?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        if (params.cashierId) searchParams.append('cashierId', params.cashierId);
        if (params.paymentMethod) searchParams.append('paymentMethod', params.paymentMethod);
        if (params.status) searchParams.append('status', params.status);
        if (params.locationId) searchParams.append('locationId', params.locationId);
        
        return {
          url: `/sales?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Sale'],
    }),
    
    getSale: builder.query<{ success: boolean; data: Sale }, string>({
      query: (id) => ({
        url: `/sales/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Sale', id }],
    }),
    
    createSale: builder.mutation<{ success: boolean; data: Sale; message: string }, CreateSaleData>({
      query: (saleData) => ({
        url: '/sales',
        method: 'POST',
        body: saleData,
      }),
      invalidatesTags: ['Sale', 'Commission', 'Inventory', 'Report', 'Product'],
    }),
    
    getSalesSummary: builder.query<SalesSummary, {
      startDate?: string;
      endDate?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        
        return {
          url: `/sales/summary?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Sale'],
    }),
    
    reprintSale: builder.mutation<{ success: boolean; data: Sale; message: string }, string>({
      query: (saleId) => ({
        url: `/sales/${saleId}/reprint`,
        method: 'POST',
      }),
      invalidatesTags: ['Sale'],
    }),

    // Draft endpoints
    saveDraft: builder.mutation<{ success: boolean; data: Sale; message: string }, CreateDraftData>({
      query: (draftData) => ({
        url: '/sales/drafts',
        method: 'POST',
        body: draftData,
      }),
      invalidatesTags: ['Sale'],
    }),

    getDrafts: builder.query<DraftsResponse, {
      page?: number;
      limit?: number;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        
        return {
          url: `/sales/drafts?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Sale'],
    }),

    updateDraft: builder.mutation<{ success: boolean; data: Sale; message: string }, { id: string; data: CreateDraftData }>({
      query: ({ id, data }) => ({
        url: `/sales/drafts/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Sale'],
    }),

    deleteDraft: builder.mutation<{ success: boolean; message: string }, string>({
      query: (draftId) => ({
        url: `/sales/drafts/${draftId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sale'],
    }),

    completeDraft: builder.mutation<{ success: boolean; data: Sale; message: string }, { id: string; data: CompleteDraftData }>({
      query: ({ id, data }) => ({
        url: `/sales/drafts/${id}/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Sale', 'Commission', 'Inventory', 'Report', 'Product'],
    }),
  }),
});

export const {
  useGetSalesQuery,
  useGetSaleQuery,
  useCreateSaleMutation,
  useGetSalesSummaryQuery,
  useReprintSaleMutation,
  // Draft hooks
  useSaveDraftMutation,
  useGetDraftsQuery,
  useUpdateDraftMutation,
  useDeleteDraftMutation,
  useCompleteDraftMutation,
} = salesApi;
